import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_AiAvatarGenerator = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/ai-avatar-generator',
			name: 'AI Avatar Generator — Create AI Avatars | Lift',
			description:
				'Generate professional AI avatars instantly with AI. No watermark. Upload selfies, choose your style, and get studio-quality business headshots in minutes. Try now!',
			url: 'https://liftapp.ai/ai-avatar-generator',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/ai-avatar-generator#product' },
				{ '@id': 'https://liftapp.ai/ai-avatar-generator#howto' },
				{ '@id': 'https://liftapp.ai/ai-avatar-generator#benefits' },
				{ '@id': 'https://liftapp.ai/ai-avatar-generator#faq' },
				{ '@id': 'https://liftapp.ai/ai-avatar-generator#why-lift' },
				{ '@id': 'https://liftapp.ai/ai-avatar-generator#explore-tools' },
				{ '@id': 'https://liftapp.ai/ai-avatar-generator#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/ai-avatar-generator#product',
			name: 'AI Avatar Generator Online',
			description:
				'Create professional AI avatars and business headshots in minutes. Get studio-quality results from selfies, no watermarks. Try Lift AI Avatar Generator now.',
			url: 'https://liftapp.ai/ai-avatar-generator',
			fileFormat: ['image/jpeg', 'image/png', 'image/webp'],
			memoryRequirements: 'Max file size: 20MB',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/ai-avatar-generator#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload your photos',
					text: "Choose a photo from your device or take one using Lift's app",
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Let the AI do the work',
					text: 'Watch as Lift generates professional AI avatars automatically',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Customize adjustments',
					text: 'Fine-tune your image with easy-to-use editing tools',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download and use',
					text: 'Download your image or publish it straight to your social feeds',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-avatar-generator#benefits',
			name: 'Who can benefit from AI Avatar Generator?',
			description:
				"Lift's AI Avatar Generator is useful for professionals, job seekers, creators, and businesses who want professional AI avatars and headshots for LinkedIn, portfolios, social media, and personal branding.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Job Seekers & Career Switchers — Upgrade your LinkedIn and resume with professional AI headshots that boost credibility. Stand out with polished, confidence-building avatars that make strong first impressions.',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Freelancers & Solopreneurs — Build your personal brand with high-quality AI avatars for websites, portfolios, and social profiles. Save money on expensive photo shoots while maintaining professional consistency.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: "Remote Teams — Unify your company's visual identity with consistent professional headshots for all team members. Perfect for About Us pages, team directories, and corporate profiles without scheduling logistics.",
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Personal Brand Builders — Refresh your creator profiles with eye-catching, professional AI avatars that stay on-brand. Ideal for podcasts, YouTube, online courses, and social media presence.',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Students & educators — Create professional headshots for academic profiles, presentations, and portfolios without studio sessions. Generate multiple styles for different contexts, from formal to creative applications.',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'Avatar from photo online use cases',
				description:
					'Example of how Lift AI Avatar Generator creates professional AI avatars and headshots for various use cases.',
			},
		},

		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/ai-avatar-generator#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is Lift AI Avatar Generator?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift AI Avatar Generator is a premium online tool that instantly creates professional business headshots and AI avatars with studio-quality precision — ideal for LinkedIn profiles, corporate websites, and personal branding.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does Lift AI Avatar Generator work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Simply upload a few selfies to Lift's AI avatar maker tool. Our AI automatically generates professional headshots in your desired styles and backgrounds in minutes — no photography experience or physical photo shoot needed. Download your AI generated avatars with no watermark.",
					},
				},
				{
					'@type': 'Question',
					name: 'Will it keep hair, edges, and fine details?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. Lift AI Avatar Generator uses advanced edge detection to preserve delicate elements like hair, fur, shadows, and soft textures — so you get clean, natural-looking cutouts without manual touch-ups.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I download the results without a watermark?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Absolutely. All images generated or edited in Lift can be exported without watermark when you're on the Pro plan. Download them in high-res PNG or JPG — ready for social media, print, ads, or e-commerce.",
					},
				},
			],
		},

		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-avatar-generator#why-lift',
			name: "Why Lift's AI Avatar Generator stands out",
			description:
				"Lift's AI Avatar Generator combines professional-grade AI, ease of use, and affordability — making it a trusted choice for millions of users worldwide.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.ae83a89c.webp',
				name: 'Avatar generation with AI',
				description:
					"Comparison of Lift's AI Avatar Generator benefits — high-quality results, intelligent rendering, multi-platform access, and cost savings.",
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-avatar-generator#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},

		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/ai-avatar-generator#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},

		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'AI Avatar Generator',
					item: 'https://liftapp.ai/ai-avatar-generator',
				},
			],
		},
	],
};
